import { z } from 'zod';

import { apenasDigitos, telefoneBrValido } from '../shared/documento';
import { defineEndpoint } from './endpoint';

// Subconta Asaas e repasse automático do parceiro (docs/plans/2026-10-27-carteira-vendedor/SUBCONTA-ASAAS.md).

/** Valores de `parceiros.asaas_status`; nulo = nada criado ("Sem repasse automático"). */
export const SITUACOES_CONTA_ASAAS = ['criando', 'erro', 'documentos', 'analise', 'aprovada', 'reprovada', 'vencida'] as const;
export const SituacaoContaAsaas = z.enum(SITUACOES_CONTA_ASAAS);
export type SituacaoContaAsaas = z.infer<typeof SituacaoContaAsaas>;

const digitos = z.string().transform(apenasDigitos);

/** O `companyType` que o Asaas exige para abrir conta de CNPJ. */
export const TIPOS_DE_EMPRESA_ASAAS = ['MEI', 'LIMITED', 'INDIVIDUAL', 'ASSOCIATION'] as const;
export type TipoDeEmpresaAsaas = (typeof TIPOS_DE_EMPRESA_ASAAS)[number];

export const CriarSubcontaInput = z.strictObject({
  email: z.string().trim().toLowerCase().pipe(z.email('E-mail inválido').max(255)),
  celular: digitos.refine(telefoneBrValido, { message: 'Confira o celular: com DDD, 10 ou 11 números.' }),
  faturamento_mensal: z.number().positive('Informe o faturamento mensal'),
  tipo_empresa: z.enum(TIPOS_DE_EMPRESA_ASAAS, 'Escolha o tipo de empresa'),
  cep: digitos.refine((v) => v.length === 8, { message: 'CEP inválido' }),
  logradouro: z.string().trim().min(1, 'Falta o endereço').max(255),
  numero: z.string().trim().min(1, 'Falta o número').max(255),
  complemento: z.string().trim().max(255).nullish(),
  bairro: z.string().trim().min(1, 'Falta o bairro').max(255),
});
export type CriarSubcontaInput = z.infer<typeof CriarSubcontaInput>;

const WALLET_ID = z.uuid('Confira o Wallet ID: ele tem 36 caracteres, com hífens.');
const SENHA = z.string().min(1, 'Digite sua senha');

export const InformarWalletInput = z.strictObject({ wallet_id: WALLET_ID });
export type InformarWalletInput = z.infer<typeof InformarWalletInput>;

/** Do parceiro: a senha de quem troca é obrigatória, exceto no onboarding. */
export const InformarWalletDoParceiroInput = z.strictObject({ wallet_id: WALLET_ID, senha: SENHA.optional() });
export type InformarWalletDoParceiroInput = z.infer<typeof InformarWalletDoParceiroInput>;

export const RemoverWalletDoParceiroInput = z.strictObject({ senha: SENHA.optional() });
export type RemoverWalletDoParceiroInput = z.infer<typeof RemoverWalletDoParceiroInput>;

export const ContaAsaasDoParceiro = z.object({
  situacao: SituacaoContaAsaas.nullable(),
  repasse_ligado: z.boolean(),
  wallet_id: z.string().nullable(),
  /** Wallet da subconta criada pela rede; nulo para o parceiro (ele não vê a origem). */
  da_rede: z.boolean().nullable(),
  erro: z.string().nullable(),
  ativacao_reenviada: z.boolean(),
  /** Aviso de vencimento recebido e situação ainda `aprovada`: confirmar os dados no Asaas. */
  confirmar_dados: z.boolean(),
  pode_criar: z.boolean(),
  pode_informar_wallet: z.boolean(),
  pode_reenviar_ativacao: z.boolean(),
  pode_ligar_agora: z.boolean(),
  /** Trocar ou desligar o wallet pede a senha de quem troca (administrador do parceiro fora do onboarding, D20). */
  exige_senha: z.boolean(),
  dados_para_criar: z.object({
    razao_social: z.string(),
    cnpj: z.string(),
    email: z.string().nullable(),
    celular: z.string().nullable(),
    cep: z.string().nullable(),
    logradouro: z.string().nullable(),
    numero: z.string().nullable(),
    complemento: z.string().nullable(),
    bairro: z.string().nullable(),
  }),
});
export type ContaAsaasDoParceiro = z.infer<typeof ContaAsaasDoParceiro>;

const OK = z.object({ ok: z.literal(true) });
const PARCEIRO = z.object({ id: z.uuid() });

// Aliança, pelo id do parceiro na URL.
export const lerContaAsaasDoParceiro = defineEndpoint({ method: 'GET', path: '/api/parceiros/:id/asaas', params: PARCEIRO, output: ContaAsaasDoParceiro });
export const criarSubcontaAsaasDoParceiro = defineEndpoint({
  method: 'POST',
  path: '/api/parceiros/:id/asaas/subconta',
  params: PARCEIRO,
  body: CriarSubcontaInput,
  output: ContaAsaasDoParceiro,
});
export const informarWalletDoParceiro = defineEndpoint({
  method: 'PUT',
  path: '/api/parceiros/:id/asaas/wallet',
  params: PARCEIRO,
  body: InformarWalletInput,
  output: ContaAsaasDoParceiro,
});
export const removerWalletDoParceiro = defineEndpoint({ method: 'DELETE', path: '/api/parceiros/:id/asaas/wallet', params: PARCEIRO, output: ContaAsaasDoParceiro });
export const reenviarAtivacaoAsaasDoParceiro = defineEndpoint({ method: 'POST', path: '/api/parceiros/:id/asaas/ativacao', params: PARCEIRO, output: OK });
export const ligarRepasseAgoraDoParceiro = defineEndpoint({ method: 'POST', path: '/api/parceiros/:id/asaas/ligar', params: PARCEIRO, output: ContaAsaasDoParceiro });

// Parceiro (admin), pelo `actor.parceiroId`.
export const lerMinhaContaAsaas = defineEndpoint({ method: 'GET', path: '/api/minha-empresa/asaas', output: ContaAsaasDoParceiro });
export const criarMinhaSubcontaAsaas = defineEndpoint({ method: 'POST', path: '/api/minha-empresa/asaas/subconta', body: CriarSubcontaInput, output: ContaAsaasDoParceiro });
export const informarMeuWalletAsaas = defineEndpoint({
  method: 'PUT',
  path: '/api/minha-empresa/asaas/wallet',
  body: InformarWalletDoParceiroInput,
  output: ContaAsaasDoParceiro,
});
export const removerMeuWalletAsaas = defineEndpoint({
  method: 'DELETE',
  path: '/api/minha-empresa/asaas/wallet',
  body: RemoverWalletDoParceiroInput,
  output: ContaAsaasDoParceiro,
});
export const reenviarMinhaAtivacaoAsaas = defineEndpoint({ method: 'POST', path: '/api/minha-empresa/asaas/ativacao', output: OK });
